import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      const next =
        location.state?.next ||
        (user.role === "vendor" ? "/driver" : user.role === "admin" ? "/admin" : "/bookings");
      navigate(next);
    } catch (err) {
      toast.error(apiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-md px-5 py-16 lg:py-24">
        <h1 className="font-display text-3xl tracking-tight sm:text-4xl">{t("auth.loginTitle")}</h1>
        <p className="mt-3 text-base text-slate-600">{t("auth.loginSub")}</p>
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="mt-9 space-y-5 rounded-xl border border-slate-200 bg-white p-7 ambient"
        >
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              required
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input
              id="password"
              type="password"
              required
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="h-11 w-full bg-sea hover:bg-sea-dark"
          >
            {loading ? t("common.loading") : t("auth.signin")}
          </Button>
          <p className="text-center text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="font-medium text-sea underline">
              {t("reset.forgotLink")}
            </Link>
          </p>
          <p className="text-center text-sm text-slate-600">
            {t("auth.noAccount")}{" "}
            <Link to="/register" data-testid="go-register-link" className="font-semibold text-sea underline">
              {t("auth.signup")}
            </Link>
          </p>
        </form>
      </div>
      <Footer />
    </div>
  );
}
